import { withDatasourceCheck } from '@sitecore-jss/sitecore-jss-nextjs';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { useTheme } from 'lib/context/ThemeContext';
import { ItemFieldResult } from 'lib/graphql/types/item-field-result';
import Component from 'src/helpers/Component/Component';
import LinkWrapper from 'src/helpers/LinkWrapper/LinkWrapper';
import { SliderWrapper } from 'src/helpers/SliderWrapper/SliderWrapper';
import { NavigationWithIconsTheme } from './NavigationWithIcons.theme';
import { ComponentProps } from 'lib/types/component-props';
import { getBreakpoint, useCurrentScreenType } from 'lib/utils/get-screen-type';
import { mapItemFieldResultsToObject } from 'lib/graphql/mappers/map-item-field-results-to-object';
import { getFieldJsonValue } from 'lib/utils/get-json-field-value';

type NavigationChildItem = {
  fields?: ItemFieldResult[];
};

type NavigationWithIconsData = {
  item?: {
    fields?: ItemFieldResult[];
    children?: {
      results?: NavigationChildItem[];
    };
  };
};

export type NavigationWithIconsProps = {
  fields?: {
    data?: NavigationWithIconsData;
  };
} & ComponentProps;

const normalizePath = (url?: string) => {
  if (!url) {
    return '';
  }

  return url.trim().toLowerCase().split('#')[0].split('?')[0];
};

const NavigationWithIcons = (props: NavigationWithIconsProps): JSX.Element => {
  const { fields } = props;
  const { themeData, themeName } = useTheme(NavigationWithIconsTheme);
  const router = useRouter();
  const sliderRef = useRef<any>(null);
  const [currentPath, setCurrentPath] = useState('');

  const itemFields = useMemo(() => fields?.data?.item?.fields || [], [fields?.data?.item?.fields]);
  const componentFields = useMemo(() => mapItemFieldResultsToObject(itemFields), [itemFields]);
  const childItems = useMemo(
    () => fields?.data?.item?.children?.results || [],
    [fields?.data?.item?.children?.results]
  );
  const totalItems = childItems.length;

  const { currentScreenWidth } = useCurrentScreenType();

  const isDesktop = currentScreenWidth >= getBreakpoint('ml');
  const isTablet =
    currentScreenWidth >= getBreakpoint('md') && currentScreenWidth < getBreakpoint('ml');

  const slidesToShow = isDesktop
    ? Math.min(totalItems || 1, 8)
    : isTablet
    ? Math.min(totalItems || 1, 5)
    : Math.min(totalItems || 1, 3);

  const slidesToScroll = isDesktop ? 6 : 1;
  const showArrows = totalItems > slidesToShow;

  const sliderSettings = {
    infinite: false,
    speed: 400,
    slidesToShow,
    slidesToScroll,
    arrows: showArrows,
    dots: false,
    swipe: true,
    swipeToSlide: true,
    variableWidth: false,
    draggable: true,
    touchMove: true,
    centerMode: false,
    focusOnSelect: false,
    accessibility: false,
    waitForAnimate: false,
    touchThreshold: 8,
  };

  const activeSlideIndex = useMemo(() => {
    if (!currentPath) {
      return 0;
    }

    const activeChildIndex = childItems.findIndex((item) => {
      const linkField = getFieldJsonValue<{ value?: { href?: string } }>(item.fields, 'link');
      return !!linkField?.value?.href && normalizePath(linkField.value.href) === currentPath;
    });

    return activeChildIndex === -1 ? 0 : activeChildIndex;
  }, [childItems, currentPath]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    setCurrentPath(normalizePath(window.location.pathname));
  }, [router.asPath]);

  useEffect(() => {
    if (!sliderRef.current || currentPath === '') {
      return;
    }

    const visibleSlides = slidesToShow;
    const shouldScroll = activeSlideIndex >= visibleSlides - 1;

    if (!shouldScroll) {
      return;
    }

    const targetIndex = Math.max(activeSlideIndex - 1, 0);

    requestAnimationFrame(() => {
      sliderRef.current?.slickGoTo(targetIndex, true);
    });
  }, [currentPath, activeSlideIndex, slidesToShow]);

  if (!childItems.length) {
    return <></>;
  }

  const slides: JSX.Element[] = [];

  childItems.forEach((item, index) => {
    const linkField = getFieldJsonValue<{ value?: { href?: string } }>(item.fields, 'link');
    const iconField = getFieldJsonValue<{ value?: { src?: string; alt?: string } }>(
      item.fields,
      'icon'
    );
    const linkTitleField = getFieldJsonValue<{ value?: string }>(item.fields, 'linkTitle');

    if (!linkField?.value?.href) {
      return;
    }

    const iconSrc = iconField?.value?.src;
    const iconAlt = iconField?.value?.alt || linkTitleField?.value || 'Navigation icon';
    const linkHref = linkField.value.href;
    const isActive = currentPath !== '' && normalizePath(linkHref) === currentPath;

    const isFirstNavItem = index === 0;

    const navItemClasses = [
      themeData.classes.navItem,
      isFirstNavItem ? themeData.classes.firstNavItem : '',
      isActive ? themeData.classes.activeItem : '',
    ]
      .filter(Boolean)
      .join(' ');

    const labelClasses = isFirstNavItem
      ? themeData.classes.firstNavItemLabel
      : themeData.classes.label;

    const iconClasses = isFirstNavItem
      ? `${themeData.classes.iconImage} ${themeData.classes.firstNavItemIcon}`
      : themeData.classes.iconImage;

    slides.push(
      <div key={`nav-item-${index}`} className={themeData.classes.slide}>
        <LinkWrapper field={linkField}>
          <div className={navItemClasses} data-active={isActive}>
            {iconSrc && <img src={iconSrc} alt={iconAlt} className={iconClasses} />}
            <span className={labelClasses}>{linkTitleField?.value}</span>
          </div>
        </LinkWrapper>
      </div>
    );
  });

  if (!slides.length) {
    return <></>;
  }

  return (
    <Component
      variant="lg"
      dataComponent="site/navigationwithicons"
      {...props}
      fields={componentFields}
    >
      <div className={themeData.classes.wrapper}>
        <SliderWrapper sliderSettings={sliderSettings} sliderRef={sliderRef} theme={themeName}>
          {slides}
        </SliderWrapper>
      </div>
    </Component>
  );
};

export default withDatasourceCheck()<NavigationWithIconsProps>(NavigationWithIcons);
